import { useSyncExternalStore } from "react";
import { currentPeriod } from "./format";
import type { RateRow } from "./rate-api";
import { computeCutiAdjustedTotalsForRateRow, parseRateTaxItems, taxTotalFromRate } from "./rate-row-helpers";

export type Employee = {
  id: string;
  name: string;
  position: string;
  department: string;
  bpjsKesehatanNo?: string;
  bpjsTkNo?: string;
};

export type EmployeePeriodPayroll = {
  hourlyRate: number;
  baseHoursWorked: number;
  adjustmentHours: number;
  leaveHours: number;
  hoursWorked: number;
  activityPercentage: number;
};

export type PeriodGenerationStatus = {
  hoursAdjusted: boolean;
  leaveAdjusted: boolean;
  completedAt: string | null;
};

export type BpjsConfig = {
  kesehatanEmployee: number;
  kesehatanCompany: number;
  ketenagakerjaanEmployee: number;
  ketenagakerjaanCompany: number;
};

export type TaxBracket = { upTo: number | null; rate: number };

export type TaxConfig = {
  ptkpAnnual: number;
  brackets: TaxBracket[];
};

export type Insentif = {
  id: string;
  employeeId: string;
  type: "jabatan" | "wfo";
  amount: number;
  note?: string;
};

export type Reimburse = {
  id: string;
  employeeId: string;
  amount: number;
  note?: string;
  createdAt: string;
};

export type WorkAdjustment = {
  id: string;
  period: string;
  employeeId: string;
  category: "hours" | "leave";
  action: "add" | "subtract";
  amount: number;
  source: "telegram-hours" | "telegram-leave";
  note: string;
  createdAt: string;
  beforeHours: number;
  afterHours: number;
};

export type HivedeskUploadMeta = {
  fileName: string;
  uploadedAt: string;
  rowCount: number;
  matchedCount: number;
  unmatchedIdentifiers: string[];
};

export type State = {
  period: string;
  payrollPeriods: string[];
  employees: Employee[];
  payrollByPeriod: Record<string, Record<string, EmployeePeriodPayroll>>;
  periodGenerationStatus: Record<string, PeriodGenerationStatus>;
  bpjs: BpjsConfig;
  tax: TaxConfig;
  insentif: Insentif[];
  reimburse: Reimburse[];
  workAdjustments: WorkAdjustment[];
  hivedeskUpload: HivedeskUploadMeta | null;
};

const KEY = "hr-dashboard-state-v2";
const LEGACY_KEY = "hr-dashboard-state-v1";

function defaultPeriodPayroll(partial?: Partial<EmployeePeriodPayroll>): EmployeePeriodPayroll {
  const baseHoursWorked = Math.max(0, Number(partial?.baseHoursWorked ?? 0) || 0);
  const adjustmentHours = Number(partial?.adjustmentHours ?? 0) || 0;
  const leaveHours = Math.max(0, Number(partial?.leaveHours ?? 0) || 0);
  const computedHours = Math.max(0, baseHoursWorked + adjustmentHours - leaveHours);
  const hasOnlyHoursValue =
    Number.isFinite(partial?.hoursWorked as number) &&
    partial?.baseHoursWorked === undefined &&
    partial?.adjustmentHours === undefined &&
    partial?.leaveHours === undefined;
  const hoursWorked = hasOnlyHoursValue ? Math.max(0, Number(partial?.hoursWorked) || 0) : computedHours;
  const activityPercentage = Math.min(100, Math.max(0, Number(partial?.activityPercentage ?? 0) || 0));

  return {
    hourlyRate: Math.max(0, Number(partial?.hourlyRate ?? 0) || 0),
    baseHoursWorked,
    adjustmentHours,
    leaveHours,
    hoursWorked,
    activityPercentage,
  };
}

const current = currentPeriod();
const initialEmployees: Employee[] = [];
const initialPayrollByPeriod: State["payrollByPeriod"] = {
  [current]: {},
};

const initial: State = {
  period: current,
  payrollPeriods: [current],
  employees: initialEmployees,
  payrollByPeriod: initialPayrollByPeriod,
  periodGenerationStatus: {},
  bpjs: {
    kesehatanEmployee: 1,
    kesehatanCompany: 4,
    ketenagakerjaanEmployee: 2,
    ketenagakerjaanCompany: 3.7,
  },
  tax: {
    ptkpAnnual: 54_000_000,
    brackets: [
      { upTo: 60_000_000, rate: 5 },
      { upTo: 250_000_000, rate: 15 },
      { upTo: 500_000_000, rate: 25 },
      { upTo: 5_000_000_000, rate: 30 },
      { upTo: null, rate: 35 },
    ],
  },
  insentif: [],
  reimburse: [],
  workAdjustments: [],
  hivedeskUpload: null,
};

let state: State = load();
const listeners = new Set<() => void>();

function legacyPayrollFromEmployee(employee: any): EmployeePeriodPayroll {
  return defaultPeriodPayroll({
    hourlyRate: employee.hourlyRate,
    baseHoursWorked: employee.baseHoursWorked ?? employee.hoursWorked,
    adjustmentHours: employee.adjustmentHours,
    leaveHours: employee.leaveHours,
    hoursWorked: employee.hoursWorked,
    activityPercentage: employee.activityPercentage ?? 0,
  });
}

function normalizeState(loaded: State | any): State {
  const incoming = loaded || {};
  const incomingEmployees = Array.isArray(incoming.employees) ? incoming.employees : initial.employees;
  const employees: Employee[] = incomingEmployees.map((emp: any) => ({
    id: String(emp.id || uid()),
    name: String(emp.name || "").trim(),
    position: String(emp.position || "").trim(),
    department: String(emp.department || "").trim(),
    bpjsKesehatanNo: emp.bpjsKesehatanNo || "",
    bpjsTkNo: emp.bpjsTkNo || "",
  }));
  const employeeIds = new Set(employees.map((e) => e.id));
  const periodCandidates = [incoming.period, ...(incoming.payrollPeriods || [])].filter((p) => typeof p === "string" && p.trim().length > 0);
  const payrollPeriods = Array.from(new Set(periodCandidates.length > 0 ? periodCandidates : [currentPeriod()])).sort();
  const period = payrollPeriods.includes(incoming.period) ? incoming.period : payrollPeriods[0];
  const loadedPayrollByPeriod: Record<string, Record<string, EmployeePeriodPayroll>> = incoming.payrollByPeriod || {};
  const payrollByPeriod: State["payrollByPeriod"] = {};

  for (const payrollPeriod of payrollPeriods) {
    const sourceBucket = loadedPayrollByPeriod[payrollPeriod] || {};
    const bucket: Record<string, EmployeePeriodPayroll> = {};
    for (const employee of employees) {
      const sourceEntry = sourceBucket[employee.id] || null;
      const legacySource = incomingEmployees.find((e: any) => e.id === employee.id);
      bucket[employee.id] = sourceEntry
        ? defaultPeriodPayroll(sourceEntry)
        : legacySource
          ? legacyPayrollFromEmployee(legacySource)
          : defaultPeriodPayroll();
    }
    payrollByPeriod[payrollPeriod] = bucket;
  }

  const rawStatus = incoming.periodGenerationStatus || {};
  const periodGenerationStatus: State["periodGenerationStatus"] = {};
  for (const payrollPeriod of payrollPeriods) {
    const status = rawStatus[payrollPeriod] || {};
    periodGenerationStatus[payrollPeriod] = {
      hoursAdjusted: !!status.hoursAdjusted,
      leaveAdjusted: !!status.leaveAdjusted,
      completedAt: status.completedAt || null,
    };
  }

  return {
    period,
    payrollPeriods,
    employees,
    payrollByPeriod,
    periodGenerationStatus,
    bpjs: incoming.bpjs || initial.bpjs,
    tax: incoming.tax || initial.tax,
    insentif: Array.isArray(incoming.insentif) ? incoming.insentif : [],
    reimburse: Array.isArray(incoming.reimburse) ? incoming.reimburse : [],
    workAdjustments: Array.isArray(incoming.workAdjustments) ? incoming.workAdjustments : [],
    hivedeskUpload: incoming.hivedeskUpload || null,
  };
}

function load(): State {
  if (typeof window === "undefined") return initial;
  try {
    const rawV2 = localStorage.getItem(KEY);
    if (rawV2) return normalizeState({ ...initial, ...JSON.parse(rawV2) });
    const rawLegacy = localStorage.getItem(LEGACY_KEY);
    if (rawLegacy) return normalizeState({ ...initial, ...JSON.parse(rawLegacy) });
    return initial;
  } catch {
    return initial;
  }
}

function persist() {
  if (typeof window === "undefined") return;
  localStorage.setItem(KEY, JSON.stringify(state));
}

function emit() {
  persist();
  listeners.forEach((listener) => listener());
}

export function setState(updater: (s: State) => State) {
  state = normalizeState(updater(state));
  emit();
}

/** Mengganti daftar karyawan dari response GET API; payroll per ID tetap diselaraskan. */
export function applyEmployeesMasterList(employees: Employee[]) {
  setState((s) => {
    const payrollByPeriod = { ...s.payrollByPeriod };
    for (const period of s.payrollPeriods) {
      const prevBucket = payrollByPeriod[period] ?? {};
      const bucket: Record<string, EmployeePeriodPayroll> = {};
      for (const e of employees) {
        bucket[e.id] = defaultPeriodPayroll(prevBucket[e.id]);
      }
      payrollByPeriod[period] = bucket;
    }
    return { ...s, employees, payrollByPeriod };
  });
}

export function getState() {
  return state;
}

export function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useHR<T>(selector: (s: State) => T): T {
  return useSyncExternalStore(
    subscribe,
    () => selector(state),
    () => selector(initial),
  );
}

export function uid() {
  return Math.random().toString(36).slice(2, 10);
}

export function getPayrollForEmployee(period: string, employeeId: string): EmployeePeriodPayroll {
  const s = getState();
  const bucket = s.payrollByPeriod[period] || {};
  return defaultPeriodPayroll(bucket[employeeId]);
}

export function updateEmployeePayrollForPeriod(period: string, employeeId: string, patch: Partial<EmployeePeriodPayroll>) {
  setState((s) => {
    const periodBucket = s.payrollByPeriod[period] || {};
    const currentPayroll = defaultPeriodPayroll(periodBucket[employeeId]);
    const nextPayroll = defaultPeriodPayroll({ ...currentPayroll, ...patch });
    return {
      ...s,
      payrollByPeriod: {
        ...s.payrollByPeriod,
        [period]: {
          ...periodBucket,
          [employeeId]: nextPayroll,
        },
      },
    };
  });
}

export function isPeriodGenerated(period: string): boolean {
  const status = getState().periodGenerationStatus[period];
  return !!(status?.hoursAdjusted && status?.leaveAdjusted);
}

export type PayslipIncentiveItem = {
  jenis: "jabatan" | "wfo";
  nominal: number;
  catatan?: string;
};

export type PayslipReimburseItem = {
  date?: string;
  description: string;
  amount: number;
  proof?: string;
};

export type PayslipAdditionalHourItem = {
  date?: string;
  startHours?: string;
  endHours?: string;
  totalHours?: string;
  description?: string;
};

export type PayslipPaidLeave = {
  total_days: number;
  total_hours: number;
  hours_per_day: number;
  reason: string;
  proof: string;
  dates: string[];
  /** Hari cuti dengan kewajiban kerja (subset dari `dates`). */
  work_required_dates?: string[];
  amount: number;
};

export type PayslipTaxItem = {
  jenis: "pph21" | "pph23" | "lainnya";
  nominal: number;
  catatan?: string;
};

/** Label jenis pajak untuk tampilan slip gaji. */
export function payslipTaxTypeLabel(jenis: PayslipTaxItem["jenis"]): string {
  if (jenis === "pph21") return "PPh 21";
  if (jenis === "pph23") return "PPh 23";
  return "Pajak lainnya";
}

export type Payslip = {
  employee: Employee;
  period: string;
  periodStartDate?: string | null;
  periodEndDate?: string | null;
  baseHoursWorked: number;
  adjustmentHours: number;
  leaveHours: number;
  hoursWorked: number;
  activityPercentage: number;
  hourlyRate: number;
  basicSalary: number;
  insentifJabatan: number;
  insentifWFO: number;
  totalInsentif: number;
  insentifItems: PayslipIncentiveItem[];
  totalReimburse: number;
  reimburseItems: PayslipReimburseItem[];
  additionalHoursTotal: number;
  additionalHoursAmount: number;
  additionalHoursItems: PayslipAdditionalHourItem[];
  paidLeave: PayslipPaidLeave | null;
  bruto: number;
  /** Entri pajak dari kolom `rate.tax` (per periode). */
  taxItems: PayslipTaxItem[];
  totalTax: number;
  bpjsKesehatanEmp: number;
  bpjsTkEmp: number;
  totalBpjsEmp: number;
  /** @deprecated Gunakan `totalTax`; dipertahankan untuk kompatibilitas. */
  pph21Monthly: number;
  totalDeduction: number;
  netto: number;
  adjustmentNotes: string[];
};

function parseMergeRateIncentiveItems(raw: unknown): PayslipIncentiveItem[] {
  if (!raw || typeof raw !== "object") return [];
  if (typeof raw === "string") {
    try {
      return parseMergeRateIncentiveItems(JSON.parse(raw));
    } catch {
      return [];
    }
  }
  const o = raw as Record<string, unknown>;
  const items = Array.isArray(o.items) ? o.items : [];
  const out: PayslipIncentiveItem[] = [];
  for (const it of items as Record<string, unknown>[]) {
    if (!it || typeof it !== "object") continue;
    const jenis = (it.jenis ?? it.jenis_insentif) as string | undefined;
    if (jenis !== "jabatan" && jenis !== "wfo") continue;
    const nominal = Number(it.nominal ?? it.nominal_insentif ?? 0);
    if (!Number.isFinite(nominal) || nominal < 0) continue;
    const catatan = typeof it.catatan === "string" ? it.catatan : undefined;
    out.push({ jenis, nominal, catatan });
  }
  return out;
}

function parseRateReimburse(raw: unknown): { total: number; items: PayslipReimburseItem[] } {
  if (!raw) return { total: 0, items: [] };
  if (typeof raw === "string") {
    try {
      return parseRateReimburse(JSON.parse(raw));
    } catch {
      return { total: 0, items: [] };
    }
  }
  if (typeof raw !== "object") return { total: 0, items: [] };
  const o = raw as Record<string, unknown>;
  const detailsArr = Array.isArray(o.details_reimburse) ? o.details_reimburse : [];
  const items: PayslipReimburseItem[] = [];
  for (const d of detailsArr as Record<string, unknown>[]) {
    if (!d || typeof d !== "object") continue;
    const amount = Number(d.reimburse ?? d.amount ?? 0);
    if (!Number.isFinite(amount)) continue;
    items.push({
      date: typeof d.date_add === "string" ? d.date_add : typeof d.date === "string" ? d.date : undefined,
      description: String(d.description ?? d.note ?? "").trim(),
      amount,
      proof: typeof d.proof === "string" ? d.proof : undefined,
    });
  }
  const total =
    typeof o.total_reimburse === "number" && Number.isFinite(o.total_reimburse)
      ? Number(o.total_reimburse)
      : items.reduce((acc, it) => acc + it.amount, 0);
  return { total, items };
}

function parseRateAdditional(raw: unknown): { items: PayslipAdditionalHourItem[] } {
  if (!raw) return { items: [] };
  if (typeof raw === "string") {
    try {
      return parseRateAdditional(JSON.parse(raw));
    } catch {
      return { items: [] };
    }
  }
  if (typeof raw !== "object") return { items: [] };
  const o = raw as Record<string, unknown>;
  const detailsArr = Array.isArray(o.details_additing) ? o.details_additing : [];
  const items: PayslipAdditionalHourItem[] = [];
  for (const d of detailsArr as Record<string, unknown>[]) {
    if (!d || typeof d !== "object") continue;
    items.push({
      date: typeof d.date_add === "string" ? d.date_add : typeof d.date === "string" ? d.date : undefined,
      startHours: typeof d.start_hours === "string" ? d.start_hours : undefined,
      endHours: typeof d.end_hours === "string" ? d.end_hours : undefined,
      totalHours: typeof d.total_hours === "string" ? d.total_hours : undefined,
      description: typeof d.description === "string" ? d.description : typeof d.note === "string" ? d.note : undefined,
    });
  }
  return { items };
}

function parseRatePaidLeave(raw: unknown): PayslipPaidLeave | null {
  if (!raw) return null;
  if (typeof raw === "string") {
    try {
      return parseRatePaidLeave(JSON.parse(raw));
    } catch {
      return null;
    }
  }
  if (typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const dates = Array.isArray(o.dates) ? (o.dates as unknown[]).map((d) => String(d)).filter(Boolean) : [];
  if (dates.length === 0) return null;
  const hours_per_day = Number(o.hours_per_day ?? 8) || 8;
  const ymdOk = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);
  const work_required_dates = Array.isArray(o.work_required_dates)
    ? (o.work_required_dates as unknown[])
        .map((d) => String(d))
        .filter((d) => ymdOk(d) && dates.includes(d))
    : [];
  return {
    total_days: Number(o.total_days ?? dates.length) || dates.length,
    total_hours: Number(o.total_hours ?? dates.length * hours_per_day) || dates.length * hours_per_day,
    hours_per_day,
    reason: typeof o.reason === "string" ? o.reason : "",
    proof: typeof o.proof === "string" ? o.proof : "tidak terlampir",
    dates,
    work_required_dates,
    amount: 0,
  };
}

export type CalculatePayslipOptions = {
  mergeRateIncentive?: unknown;
  mergeRateTax?: unknown;
  rateReimburse?: unknown;
  rateAdditional?: unknown;
  ratePaidLeave?: unknown;
  periodStartDate?: string | null;
  periodEndDate?: string | null;
};

export function calculatePayslip(employeeId: string, options?: CalculatePayslipOptions): Payslip | null {
  const s = getState();
  const employee = s.employees.find((emp) => emp.id === employeeId);
  if (!employee) return null;
  const payroll = getPayrollForEmployee(s.period, employeeId);
  const basicSalary = payroll.hourlyRate * payroll.hoursWorked;
  const employeeInsentif = s.insentif.filter((insentif) => insentif.employeeId === employeeId);
  let insentifJabatan = employeeInsentif.filter((insentif) => insentif.type === "jabatan").reduce((sum, row) => sum + row.amount, 0);
  let insentifWFO = employeeInsentif.filter((insentif) => insentif.type === "wfo").reduce((sum, row) => sum + row.amount, 0);
  const insentifItems = parseMergeRateIncentiveItems(options?.mergeRateIncentive);
  const insentifItemsLocal: PayslipIncentiveItem[] = employeeInsentif
    .filter((row) => row.type === "jabatan" || row.type === "wfo")
    .map((row) => ({
      jenis: row.type,
      nominal: row.amount,
      catatan: row.note,
    }));
  for (const it of insentifItems) {
    if (it.jenis === "jabatan") insentifJabatan += it.nominal;
    else if (it.jenis === "wfo") insentifWFO += it.nominal;
  }
  const totalInsentif = insentifJabatan + insentifWFO;

  const reimbFromRate = parseRateReimburse(options?.rateReimburse);
  const localReimburseTotal = s.reimburse
    .filter((reimburse) => reimburse.employeeId === employeeId)
    .reduce((sum, row) => sum + row.amount, 0);
  const reimburseItems: PayslipReimburseItem[] =
    reimbFromRate.items.length > 0
      ? reimbFromRate.items
      : s.reimburse
          .filter((r) => r.employeeId === employeeId)
          .map((r) => ({ description: r.note ?? "Reimburse", amount: r.amount, date: r.createdAt }));
  const totalReimburse = reimbFromRate.items.length > 0 ? reimbFromRate.total : localReimburseTotal;

  const addParsed = parseRateAdditional(options?.rateAdditional);
  const additionalHoursTotal = payroll.adjustmentHours;
  const additionalHoursAmount = additionalHoursTotal * payroll.hourlyRate;

  const paidLeaveParsed = parseRatePaidLeave(options?.ratePaidLeave);
  const paidLeave: PayslipPaidLeave | null = paidLeaveParsed
    ? { ...paidLeaveParsed, amount: paidLeaveParsed.total_hours * payroll.hourlyRate }
    : null;

  const bruto = basicSalary + totalInsentif + totalReimburse + (paidLeave?.amount ?? 0);
  const taxItems: PayslipTaxItem[] = parseRateTaxItems(options?.mergeRateTax).map((it) => ({
    jenis: it.jenis,
    nominal: it.nominal,
    catatan: it.catatan,
  }));
  const totalTax = taxItems.length > 0 ? taxItems.reduce((a, it) => a + it.nominal, 0) : taxTotalFromRate(options?.mergeRateTax);
  const bpjsKesehatanEmp = 0;
  const bpjsTkEmp = 0;
  const totalBpjsEmp = 0;
  const pph21Monthly = totalTax;
  const totalDeduction = totalTax;
  const netto = bruto - totalDeduction;
  const adjustmentNotes = s.workAdjustments
    .filter((adjustment) => adjustment.employeeId === employeeId && adjustment.period === s.period)
    .map((adjustment) => `${adjustment.category === "hours" ? "Jam" : "Cuti"} ${adjustment.action === "add" ? "+" : "-"}${adjustment.amount}: ${adjustment.note}`);

  const combinedInsentifItems: PayslipIncentiveItem[] = [...insentifItemsLocal, ...insentifItems];

  return {
    employee,
    period: s.period,
    periodStartDate: options?.periodStartDate ?? null,
    periodEndDate: options?.periodEndDate ?? null,
    baseHoursWorked: payroll.baseHoursWorked,
    adjustmentHours: payroll.adjustmentHours,
    leaveHours: payroll.leaveHours,
    hoursWorked: payroll.hoursWorked,
    activityPercentage: payroll.activityPercentage,
    hourlyRate: payroll.hourlyRate,
    basicSalary,
    insentifJabatan,
    insentifWFO,
    totalInsentif,
    insentifItems: combinedInsentifItems,
    totalReimburse,
    reimburseItems,
    additionalHoursTotal,
    additionalHoursAmount,
    additionalHoursItems: addParsed.items,
    paidLeave,
    bruto,
    taxItems,
    totalTax,
    bpjsKesehatanEmp,
    bpjsTkEmp,
    totalBpjsEmp,
    pph21Monthly,
    totalDeduction,
    netto,
    adjustmentNotes,
  };
}

export type ParsedHivedeskRow = {
  identifier: string;
  hoursWorked: number;
  period: string;
  hourlyRate?: number;
  activityPercentage?: number;
};

export type AdjustmentInput = {
  employeeIdentifier: string;
  amount: number;
  action: "add" | "subtract";
  note: string;
};

export function upsertPayrollPeriod(period: string) {
  setState((s) => {
    if (s.payrollPeriods.includes(period)) return s;
    const templatePeriod = s.period;
    const templateBucket = s.payrollByPeriod[templatePeriod] || {};
    const nextBucket: Record<string, EmployeePeriodPayroll> = {};
    for (const employee of s.employees) {
      nextBucket[employee.id] = defaultPeriodPayroll(templateBucket[employee.id]);
    }
    return {
      ...s,
      payrollPeriods: [...s.payrollPeriods, period].sort(),
      payrollByPeriod: {
        ...s.payrollByPeriod,
        [period]: nextBucket,
      },
      periodGenerationStatus: {
        ...s.periodGenerationStatus,
        [period]: { hoursAdjusted: false, leaveAdjusted: false, completedAt: null },
      },
    };
  });
}

export function setActivePayrollPeriod(period: string) {
  setState((s) => {
    if (s.payrollPeriods.includes(period)) {
      return { ...s, period };
    }
    const templateBucket = s.payrollByPeriod[s.period] || {};
    const nextBucket: Record<string, EmployeePeriodPayroll> = {};
    for (const employee of s.employees) {
      nextBucket[employee.id] = defaultPeriodPayroll(templateBucket[employee.id]);
    }
    return {
      ...s,
      period,
      payrollPeriods: [...s.payrollPeriods, period].sort(),
      payrollByPeriod: { ...s.payrollByPeriod, [period]: nextBucket },
      periodGenerationStatus: {
        ...s.periodGenerationStatus,
        [period]: { hoursAdjusted: false, leaveAdjusted: false, completedAt: null },
      },
    };
  });
}

export function renamePayrollPeriod(from: string, to: string) {
  setState((s) => {
    const mapped = s.payrollPeriods.map((period) => (period === from ? to : period));
    const payrollPeriods = Array.from(new Set(mapped)).sort();
    const fromBucket = s.payrollByPeriod[from];
    const toBucket = s.payrollByPeriod[to] || fromBucket || {};
    const nextPayrollByPeriod = { ...s.payrollByPeriod, [to]: toBucket };
    delete nextPayrollByPeriod[from];
    const fromGeneration = s.periodGenerationStatus[from];
    const toGeneration = s.periodGenerationStatus[to] || fromGeneration || { hoursAdjusted: false, leaveAdjusted: false, completedAt: null };
    const nextGeneration = { ...s.periodGenerationStatus, [to]: toGeneration };
    delete nextGeneration[from];
    return {
      ...s,
      payrollPeriods,
      period: s.period === from ? to : s.period,
      payrollByPeriod: nextPayrollByPeriod,
      periodGenerationStatus: nextGeneration,
      workAdjustments: s.workAdjustments.map((adjustment) => (adjustment.period === from ? { ...adjustment, period: to } : adjustment)),
    };
  });
}

export function deletePayrollPeriod(period: string) {
  setState((s) => {
    if (s.payrollPeriods.length <= 1) return s;
    const nextPeriods = s.payrollPeriods.filter((item) => item !== period);
    const nextPayrollByPeriod = { ...s.payrollByPeriod };
    const nextGeneration = { ...s.periodGenerationStatus };
    delete nextPayrollByPeriod[period];
    delete nextGeneration[period];
    return {
      ...s,
      payrollPeriods: nextPeriods,
      period: s.period === period ? nextPeriods[0] : s.period,
      payrollByPeriod: nextPayrollByPeriod,
      periodGenerationStatus: nextGeneration,
    };
  });
}

export function applyHivedeskUpload(rows: ParsedHivedeskRow[], fileName: string) {
  const now = new Date().toISOString();
  setState((s) => {
    const employeeById = new Map(s.employees.map((employee) => [employee.id.toLowerCase(), employee]));
    const employeeByName = new Map(s.employees.map((employee) => [employee.name.trim().toLowerCase(), employee]));
    const unmatchedIdentifiers: string[] = [];
    const touched = new Set<string>();
    let selectedPeriod = s.period;
    for (const row of rows) {
      if (row.period) selectedPeriod = row.period;
    }
    const bucket: Record<string, EmployeePeriodPayroll> = { ...(s.payrollByPeriod[selectedPeriod] || {}) };

    for (const row of rows) {
      const key = row.identifier.trim().toLowerCase();
      const match = employeeById.get(key) || employeeByName.get(key);
      if (!match) {
        unmatchedIdentifiers.push(row.identifier);
        continue;
      }
      touched.add(match.id);
      const currentPayroll = defaultPeriodPayroll(bucket[match.id]);
      bucket[match.id] = defaultPeriodPayroll({
        ...currentPayroll,
        baseHoursWorked: Math.max(0, row.hoursWorked),
        adjustmentHours: 0,
        leaveHours: 0,
        hoursWorked: Math.max(0, row.hoursWorked),
        hourlyRate: Number.isFinite(row.hourlyRate) ? Math.max(0, Number(row.hourlyRate)) : currentPayroll.hourlyRate,
        activityPercentage: Number.isFinite(row.activityPercentage)
          ? Math.min(100, Math.max(0, Number(row.activityPercentage)))
          : currentPayroll.activityPercentage,
      });
    }

    const existingBucket = s.payrollByPeriod[selectedPeriod] || {};
    const mergedBucket = { ...existingBucket, ...bucket };
    for (const employee of s.employees) {
      if (!mergedBucket[employee.id]) {
        mergedBucket[employee.id] = defaultPeriodPayroll();
      }
    }
    const payrollPeriods = s.payrollPeriods.includes(selectedPeriod) ? s.payrollPeriods : [...s.payrollPeriods, selectedPeriod].sort();

    return {
      ...s,
      period: selectedPeriod,
      payrollPeriods,
      payrollByPeriod: {
        ...s.payrollByPeriod,
        [selectedPeriod]: mergedBucket,
      },
      periodGenerationStatus: {
        ...s.periodGenerationStatus,
        [selectedPeriod]: { hoursAdjusted: false, leaveAdjusted: false, completedAt: null },
      },
      workAdjustments: s.workAdjustments.filter((adjustment) => adjustment.period !== selectedPeriod),
      hivedeskUpload: {
        fileName,
        uploadedAt: now,
        rowCount: rows.length,
        matchedCount: touched.size,
        unmatchedIdentifiers,
      },
    };
  });
}

export function mergePayrollSnapshotFromRates(payrollKey: string, rates: RateRow[]) {
  const now = new Date().toISOString();

  function hmsToSecondsStr(s: string): number {
    const parts = String(s ?? "").split(":").map((x) => Number(x));
    if (parts.length !== 3) return 0;
    const [hh, mm, ss] = parts;
    if (![hh, mm, ss].every((n) => Number.isFinite(n) && n >= 0)) return 0;
    return hh * 3600 + mm * 60 + ss;
  }

  function hhDotMmToSeconds(raw: unknown): number {
    const str = String(raw ?? "").trim();
    if (!str) return 0;
    const m = str.match(/^(\d{1,3})\.(\d{1,2})$/);
    if (!m) return 0;
    const hh = Number(m[1]);
    const mm = Number(m[2]);
    if (![hh, mm].every((n) => Number.isFinite(n) && n >= 0)) return 0;
    if (mm >= 60) return 0;
    return hh * 3600 + mm * 60;
  }

  function safeAdditionalPayload(raw: unknown): {
    total_hours_additing?: string;
    details_additing?: Array<{ total_hours?: string }>;
  } | null {
    if (raw === null || raw === undefined) return null;
    if (typeof raw === "string") {
      try {
        return safeAdditionalPayload(JSON.parse(raw));
      } catch {
        return null;
      }
    }
    if (typeof raw !== "object") return null;
    const o = raw as any;
    return {
      total_hours_additing: typeof o.total_hours_additing === "string" ? o.total_hours_additing : undefined,
      details_additing: Array.isArray(o.details_additing) ? o.details_additing : undefined,
    };
  }

  function safeDetailsTotals(details: unknown): { loggedTime?: unknown; activityPct?: unknown } {
    if (!details || typeof details !== "object") return {};
    const d = details as any;
    const totals = d.totals && typeof d.totals === "object" ? d.totals : {};
    return { loggedTime: totals.loggedTime, activityPct: totals.activityPct ?? totals.activitypct };
  }

  setState((s) => {
    const employeeByName = new Map(s.employees.map((employee) => [employee.name.trim().toLowerCase(), employee]));
    const bucket = { ...(s.payrollByPeriod[payrollKey] || {}) };

    for (const r of rates) {
      const employee = employeeByName.get(String(r.name_employee ?? "").trim().toLowerCase());
      if (!employee) continue;

      const adj = computeCutiAdjustedTotalsForRateRow(r);
      const baseHours = adj.adjustedLoggedSeconds / 3600;
      const adjustmentHours = adj.adjustedAddSeconds / 3600;

      const totalsWrap = safeDetailsTotals(r.details_project);
      const activityRaw = totalsWrap.activityPct;
      const activityPct =
        adj.adjustedActivityPct ??
        (typeof activityRaw === "number" && Number.isFinite(activityRaw)
          ? Math.min(100, Math.max(0, activityRaw))
          : typeof r.total_activity === "number" && Number.isFinite(r.total_activity)
            ? Math.min(100, Math.max(0, r.total_activity))
            : defaultPeriodPayroll(bucket[employee.id]).activityPercentage);

      const current = defaultPeriodPayroll(bucket[employee.id]);
      const hourlyRate =
        typeof r.rate_hours === "number" && Number.isFinite(r.rate_hours) && r.rate_hours > 0
          ? r.rate_hours
          : current.hourlyRate;

      bucket[employee.id] = defaultPeriodPayroll({
        ...current,
        hourlyRate,
        baseHoursWorked: baseHours,
        adjustmentHours,
        activityPercentage: activityPct,
      });
    }

    return {
      ...s,
      payrollByPeriod: {
        ...s.payrollByPeriod,
        [payrollKey]: bucket,
      },
      periodGenerationStatus: {
        ...s.periodGenerationStatus,
        [payrollKey]: {
          hoursAdjusted: true,
          leaveAdjusted: true,
          completedAt: now,
        },
      },
    };
  });
}

export function applyTelegramAdjustments(
  category: "hours" | "leave",
  entries: AdjustmentInput[],
  targetPayrollPeriod?: string,
) {
  const now = new Date().toISOString();
  setState((s) => {
    const periodKey = targetPayrollPeriod ?? s.period;
    const employeeById = new Map(s.employees.map((employee) => [employee.id.toLowerCase(), employee]));
    const employeeByName = new Map(s.employees.map((employee) => [employee.name.trim().toLowerCase(), employee]));
    const bucket = { ...(s.payrollByPeriod[periodKey] || {}) };
    const adjustments: WorkAdjustment[] = [];

    for (const entry of entries) {
      const key = entry.employeeIdentifier.trim().toLowerCase();
      const employee = employeeById.get(key) || employeeByName.get(key);
      if (!employee) continue;
      const currentPayroll = defaultPeriodPayroll(bucket[employee.id]);
      const amount = Math.max(0, Number(entry.amount) || 0);
      const signedAmount = entry.action === "add" ? amount : -amount;
      const beforeHours = currentPayroll.hoursWorked;
      let nextPayload: EmployeePeriodPayroll;

      if (category === "hours") {
        nextPayload = defaultPeriodPayroll({
          ...currentPayroll,
          adjustmentHours: currentPayroll.adjustmentHours + signedAmount,
        });
      } else {
        nextPayload = defaultPeriodPayroll({
          ...currentPayroll,
          leaveHours: entry.action === "add" ? Math.max(0, currentPayroll.leaveHours - amount) : currentPayroll.leaveHours + amount,
        });
      }
      bucket[employee.id] = nextPayload;
      adjustments.push({
        id: uid(),
        period: periodKey,
        employeeId: employee.id,
        category,
        action: entry.action,
        amount,
        source: category === "hours" ? "telegram-hours" : "telegram-leave",
        note: entry.note,
        createdAt: now,
        beforeHours,
        afterHours: nextPayload.hoursWorked,
      });
    }

    const currentStatus = s.periodGenerationStatus[periodKey] || { hoursAdjusted: false, leaveAdjusted: false, completedAt: null };
    const nextStatus: PeriodGenerationStatus = {
      hoursAdjusted: currentStatus.hoursAdjusted || category === "hours",
      leaveAdjusted: currentStatus.leaveAdjusted || category === "leave",
      completedAt: null,
    };
    if (nextStatus.hoursAdjusted && nextStatus.leaveAdjusted) {
      nextStatus.completedAt = now;
    }

    return {
      ...s,
      payrollByPeriod: {
        ...s.payrollByPeriod,
        [periodKey]: bucket,
      },
      periodGenerationStatus: {
        ...s.periodGenerationStatus,
        [periodKey]: nextStatus,
      },
      workAdjustments: [...s.workAdjustments, ...adjustments],
    };
  });
}